import { memo, useCallback, useEffect, useState } from 'react';

import { isAndroid, isIOS } from 'react-device-detect';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';

import TopBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/TopBtn';
import HistoryBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/HistoryBtn';
import ShortFormBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ShortFormBtn';
import StyleClipAddBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/StyleClipAddBtn';
import EvtShareBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/EvtShareBtn';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import {
  IconHomeText,
  IconHomeTextOn,
  IconHomeTextDark,
  IconHomeTextDarkOn,
  IconCateText,
  IconCateTextOn,
  IconCateTextDark,
  IconCateTextDarkOn,
  IconSearchText,
  IconSearchTextOn,
  IconSearchTextDark,
  IconSearchTextDarkOn,
  IconHeartText,
  IconHeartTextOn,
  IconHeartTextDark,
  IconHeartTextDarkOn,
  IconMyText,
  IconMyTextOn,
  IconMyTextDark,
  IconMyTextDarkOn,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';

import * as S from './style';

interface IProps {
  /** active */
  active?: string;
  /** bgType */
  bgType?: 'white' | 'black' | undefined;
  /** isTopBtn */
  isTopBtn?: boolean;
  /** isHistoryBtn */
  isHistoryBtn?: boolean;
  /** isStyleClipAddBtn */
  isStyleClipAddBtn?: boolean;
  /** isShortFormBtn */
  isShortFormBtn?: boolean | 'simple';
  /** isShortFormSimpleBtn */
  isShortFormSimpleBtn?: boolean;
  /** isShareBtn */
  isShareBtn?: boolean;
  /** styleClipTooltipPoint */
  styleClipTooltipPoint?: number;
  /** isNativeAppBoth */
  isNativeAppBoth?: boolean;
  /** isIPhoneXApp */
  isIPhoneXApp?: boolean;
  /** historyImg */
  historyImg?: string;
  /** isShortFormBtnTooltip */
  isShortFormBtnTooltip?: boolean;
  /** setTooltipShown */
  setTooltipShown?: (value: string) => void;
  /** getHistoryImg */
  getHistoryImg?: () => void;
  /** onClickTop */
  onClickTop?: () => void;
  /** onClickShortForm */
  onClickShortForm?: () => void;
  /** onClickHome */
  onClickHome?: () => void;
  /** onClickCate */
  onClickCate?: () => void;
  /** onClickDiscovery */
  // onClickDiscovery?: () => void;
  /** onClickSearch */
  onClickSearch?: () => void;
  /** onClickMyheart */
  onClickMyheart?: () => void;
  /** onClickMypage */
  onClickMypage?: () => void;
  /** onClickHistory */
  onClickHistory?: () => void;
  /** onClickStyleClipAddBtn */
  onClickStyleClipAddBtn?: () => void;
  /** onClickShare */
  onClickShare?: () => Promise<void>;
  isLogin?: boolean;
  eventType?: string;
  appVersion?: string;
  historyBtnRs?: string;
}

type ActionbarMenuType = 'main' | 'category' | 'search' | 'like' | 'myPage';

const Actionbar = ({
  active: externalActive,
  bgType = 'white',
  isTopBtn = true,
  isHistoryBtn = true,
  isStyleClipAddBtn = false,
  isShortFormBtn = false,
  isShortFormSimpleBtn = false,
  isShareBtn = false,
  isNativeAppBoth,
  isIPhoneXApp,
  historyImg,
  isShortFormBtnTooltip,
  styleClipTooltipPoint,
  historyBtnRs,
  setTooltipShown,
  onClickTop,
  getHistoryImg,
  onClickHistory,
  onClickShortForm,
  onClickHome,
  onClickCate,
  // onClickDiscovery,
  onClickSearch,
  onClickMyheart,
  onClickMypage,
  onClickStyleClipAddBtn,
  onClickShare,
  eventType,
  appVersion,
  isLogin,
}: IProps) => {
  const [cName, setClassName] = useState('ready');
  const [topBtnActive, setTopBtnActive] = useState(false);
  const [active, setActive] = useState(externalActive || '');

  const isDarkMode = bgType === 'black';

  useEffect(() => {
    setClassName(
      classNameBind([
        'actionbar',
        isIPhoneXApp ? 'is-old-app-iphonex' : '',
        isNativeAppBoth && isAndroid ? 'is-andrd' : '',
        isNativeAppBoth && isIOS ? 'is-ios' : '',
      ])
    );
  }, [isNativeAppBoth, isIPhoneXApp]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isTopBtn) {
      const handleScroll = () => {
        const scrollY = -document.body.getBoundingClientRect().top;

        if (scrollY > 1) {
          setTopBtnActive(true);
        } else {
          setTopBtnActive(false);
        }
      };

      const scrollListener = () => {
        window.addEventListener('scroll', handleScroll);
      };
      scrollListener();
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isTopBtn]);

  useEffect(() => {
    const handlePopState = () => {
      // 뒤로가기 이벤트 발생 시 실행할 함수
      if (getHistoryImg) getHistoryImg();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // 페이지가 BFCache에서 로드될 때 실행할 함수
      if (event.persisted) {
        if (getHistoryImg) getHistoryImg();
      }
    };

    const handleVisibilityChange = () => {
      // 페이지 가시성이 변경될 때 실행할 함수
      if (getHistoryImg) getHistoryImg();
    };

    // popstate 및 pageshow 이벤트 리스너 등록
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('visibilitychange', handleVisibilityChange);

    // 컴포넌트 마운트 시 getHistoryImg 실행
    getHistoryImg?.();

    // active 우선순위:
    // 1) 외부에서 내려준 active
    // 2) 현재 pathname 기반
    if (externalActive) {
      setActive(externalActive);
    } else {
      const path = window.location.pathname;
      const isMainPage = path.includes('/rn/women');
      const isLnbPage =
        path.includes('/rn/lnb') || path.includes('/rn/designer');
      const isDiscoveryPage = path.includes('/rn/discovery');
      const isShortformPage = path.includes('/shortform');
      const isSearchPage = path.includes('/search');
      const isMyheartPage = path.includes('/rn/myheart');
      const isMypagePage = path.includes('/rn/mypage');

      if (isMainPage) setActive('main');
      else if (isLnbPage) setActive('category');
      else if (isSearchPage) setActive('search');
      else if (isShortformPage) setActive('shortform');
      else if (isDiscoveryPage) setActive('discovery');
      else if (isMyheartPage) setActive('like');
      else if (isMypagePage) setActive('myPage');
      else setActive('main');
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [externalActive, getHistoryImg]);

  // EVENT HANDLER : 숏폼 버튼 클릭
  const onClickShortFormBtn = useCallback(() => {
    if (onClickShortForm) onClickShortForm();
  }, [onClickShortForm]);

  // EVENT HANDLER : 탑 버튼 클릭
  const onClickTopBtn = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (onClickTop) onClickTop();
  }, [onClickTop]);

  // ITEM RENDERER : getActBtnSet
  const isActiveMenu = useCallback(
    (menuType: ActionbarMenuType) => {
      if (menuType === 'main') {
        return (
          active === 'main' || active === 'discovery' || active === 'shortform'
        );
      }

      return active === menuType;
    },
    [active]
  );

  const getActionbarIcon = useCallback(
    (menuType: ActionbarMenuType) => {
      const isActive = isActiveMenu(menuType);

      switch (menuType) {
        case 'main':
          if (isDarkMode) {
            return isActive ? <IconHomeTextDarkOn /> : <IconHomeTextDark />;
          }
          return isActive ? <IconHomeTextOn /> : <IconHomeText />;

        case 'category':
          if (isDarkMode) {
            return isActive ? <IconCateTextDarkOn /> : <IconCateTextDark />;
          }
          return isActive ? <IconCateTextOn /> : <IconCateText />;

        case 'search':
          if (isDarkMode) {
            return isActive ? <IconSearchTextDarkOn /> : <IconSearchTextDark />;
          }
          return isActive ? <IconSearchTextOn /> : <IconSearchText />;

        case 'like':
          if (isDarkMode) {
            return isActive ? <IconHeartTextDarkOn /> : <IconHeartTextDark />;
          }
          return isActive ? <IconHeartTextOn /> : <IconHeartText />;

        case 'myPage':
          if (isDarkMode) {
            return isActive ? <IconMyTextDarkOn /> : <IconMyTextDark />;
          }
          return isActive ? <IconMyTextOn /> : <IconMyText />;

        default:
          return <IconHomeText />;
      }
    },
    [isDarkMode, isActiveMenu]
  );

  const getActBtnSet = useCallback(
    () => (
      <div className="float-btn">
        {isTopBtn && (
          <TopBtn
            onClick={onClickTopBtn}
            className={topBtnActive ? 'active' : ''}
          />
        )}
        {isShareBtn && <EvtShareBtn type="share" onClick={onClickShare} />}
        {isHistoryBtn && (
          <HistoryBtn
            rs={historyBtnRs}
            onClick={onClickHistory}
            imgSrc={isLogin ? historyImg : ''}
          />
        )}
        {isStyleClipAddBtn && (
          <StyleClipAddBtn
            onClick={onClickStyleClipAddBtn}
            styleClipTooltipPoint={styleClipTooltipPoint}
            isShortFormBtnTooltip={isShortFormBtnTooltip}
            setTooltipShown={setTooltipShown}
          />
        )}
        {isShortFormBtn && (
          <ShortFormBtn
            onClick={onClickShortFormBtn}
            isSimple={isShortFormBtn === 'simple'}
          />
        )}
      </div>
    ),
    [
      isTopBtn,
      onClickTopBtn,
      topBtnActive,
      isHistoryBtn,
      historyBtnRs,
      onClickHistory,
      isLogin,
      historyImg,
      isStyleClipAddBtn,
      onClickStyleClipAddBtn,
      styleClipTooltipPoint,
      isShortFormBtnTooltip,
      setTooltipShown,
      isShortFormBtn,
      onClickShortFormBtn,
      isShareBtn,
      onClickShare,
    ]
  );

  // ITEM RENDERER : getActionbarMain
  const getActionbarMain = useCallback(
    () => (
      <div className="actionbar-main">
        <nav>
          <ul>
            <li
              className={classNameBind([
                'main',
                isActiveMenu('main') ? 'active' : '',
              ])}
            >
              <Button onClick={onClickHome}>
                <span className="icon">{getActionbarIcon('main')}</span>
              </Button>
            </li>

            <li
              className={classNameBind([
                'category',
                isActiveMenu('category') ? 'active' : '',
              ])}
            >
              <Button onClick={onClickCate}>
                <span className="icon">{getActionbarIcon('category')}</span>
              </Button>
            </li>

            <li
              className={classNameBind([
                'search',
                isActiveMenu('search') ? 'active' : '',
              ])}
            >
              <Button onClick={onClickSearch}>
                <span className="icon">{getActionbarIcon('search')}</span>
              </Button>
            </li>

            <li
              className={classNameBind([
                'like',
                isActiveMenu('like') ? 'active' : '',
              ])}
            >
              <Button onClick={onClickMyheart}>
                <span className="icon">{getActionbarIcon('like')}</span>
              </Button>
            </li>

            <li
              className={classNameBind([
                'myPage',
                isActiveMenu('myPage') ? 'active' : '',
              ])}
            >
              <Button onClick={onClickMypage}>
                <span className="icon">{getActionbarIcon('myPage')}</span>
              </Button>
            </li>
          </ul>
        </nav>

        {getActBtnSet()}
      </div>
    ),
    [
      onClickHome,
      onClickCate,
      onClickSearch,
      eventType,
      onClickMyheart,
      onClickMypage,
      isActiveMenu,
      getActionbarIcon,
      getActBtnSet,
    ]
  );

  return (
    <>
      {cName !== 'ready' ? (
        <S.Actionbar id="actionbar" className={cName} $bgType={bgType}>
          <div className="inner-actionbar">{getActionbarMain()}</div>
        </S.Actionbar>
      ) : null}
    </>
  );
};

export default memo(Actionbar);
